import {
  ErrorHandler,
  type EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';

import { environment } from '../../../environments/environment';
import { initWebVitals } from './web-vitals';

/**
 * Providers do Sentry específicos do Angular:
 *
 * - `ErrorHandler` substituído por `Sentry.createErrorHandler` —
 *   captura erros não-tratados (incl. throws em zoneless / signals).
 * - `Sentry.TraceService` — instrumenta o `Router` pra criar transactions
 *   em navegações (route changes viram performance spans no Sentry).
 * - `initWebVitals()` no app initializer — começa a coletar logo no boot.
 *
 * Sentry.init() em si é chamado em `main.ts` antes do bootstrap (ver
 * sentry.config.ts) — providers aqui assumem que init já rodou.
 */
export function provideSentry(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        // Não mostra dialog "report a bug" — UX ruim em produto BR.
        showDialog: false,
        // Em dev, também loga no console pra não silenciar erros locais.
        logErrors: !environment.production,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      // Força instanciação do TraceService (efeito colateral: registra
      // listener no Router). Sem isso, navegações não viram transactions.
      inject(Sentry.TraceService);
      initWebVitals();
    }),
  ]);
}
